import { basketMissOffset, basketSceneLayout as L, type BasketShotImpact } from '../three/basketSceneLayout';

export type BasketAimPoint = { x: number; y: number };
export type BasketAimVector = { x: number; y: number; z: number };
/** Çizginin bittiği noktanın pota düzlemindeki yeri. `short`: potaya ulaşmayan veya geriye sürükleme; atış sayılmaz. */
export type BasketAimZone = 'basket' | 'rim' | 'backboard' | 'airball' | 'short';
/** Sunum sonucu: `swish` basket, diğerleri ıska. */
export type BasketShotOutcome = BasketShotImpact | 'airball';
export type BasketShotPlan = {
  outcome: BasketShotOutcome;
  impact: BasketAimVector;
  apexY: number;
  /** Iskada topun sekeceği yatay yön (-1 sol, 1 sağ). */
  missDirection: number;
};

export const basketAimRules = {
  /** Bundan kısa sürükleme atış sayılmaz (CSS piksel). */
  minDragPx: 36,
  /** Sürüklemenin başlayabileceği, topun ekran merkezine uzaklık (CSS piksel). */
  grabRadiusPx: 96,
  /** Bu kadar ıskadan sonra pota hedef bölgesi ipucu olarak parlar. */
  hintAfterMisses: 3,
  /** Klavye nişangâhının bir tuş basışındaki hareketi (metre). */
  keyboardStep: 0.25,
  /** Rapora yazılan deneme sayısının üst sınırı; oyunda deneme sınırsızdır. */
  maxRecordedAttempts: 999,
  /** Basket bölgesi: çember çizgisinin çevresi ve altındaki file (pota düzleminde, metre). */
  basketHalfWidth: 1.0,
  basketBelow: 1.3,
  basketAbove: 0.6,
  /** Basket bölgesini saran çember bandı. */
  rimHalfWidth: 1.45,
  rimBelow: 1.55,
  rimAbove: 0.9,
  /** Bu yükseklikten aşağıda biten çizgi potaya ulaşmamış sayılır. */
  minReachY: 2.2,
} as const;

const R = basketAimRules;
const BOARD_HALF = L.backboard.width / 2;
const BOARD_TOP = L.backboard.y + L.backboard.height / 2;

/** Kamera ışınının çemberin merkezinden geçen dikey düzlemi kestiği nokta; ışın potaya doğru değilse null. */
export function intersectHoopPlane(origin: BasketAimVector, direction: BasketAimVector): BasketAimPoint | null {
  if (direction.z >= -1e-6) return null;
  const distance = (L.hoop.z - origin.z) / direction.z;
  if (distance <= 0) return null;
  return { x: origin.x + direction.x * distance, y: origin.y + direction.y * distance };
}

export function classifyBasketAim(point: BasketAimPoint | null): BasketAimZone {
  if (!point || point.y < R.minReachY) return 'short';
  const dx = Math.abs(point.x - L.hoop.x); const dy = point.y - L.hoop.y;
  if (dx <= R.basketHalfWidth && dy >= -R.basketBelow && dy <= R.basketAbove) return 'basket';
  if (dx <= R.rimHalfWidth && dy >= -R.rimBelow && dy <= R.rimAbove) return 'rim';
  if (dx <= BOARD_HALF && dy > 0 && point.y <= BOARD_TOP) return 'backboard';
  return 'airball';
}

function clamp(value: number, min: number, max: number) { return Math.min(max, Math.max(min, value)); }

/** Öğrencinin çizdiği noktaya giden atış planı. Basket bölgesi her zaman file ile biter. */
export function buildBasketShotPlan(point: BasketAimPoint, zone: Exclude<BasketAimZone, 'short'>): BasketShotPlan {
  const side = point.x >= L.hoop.x ? 1 : -1;
  if (zone === 'basket') return { outcome: 'swish', impact: { ...L.hoop }, apexY: L.flightApexY, missDirection: side };
  if (zone === 'rim') {
    return { outcome: 'rim', impact: { x: L.hoop.x + side * 0.86, y: L.hoop.y, z: L.hoop.z }, apexY: L.flightApexY, missDirection: side };
  }
  if (zone === 'backboard') {
    return { outcome: 'backboard', impact: { x: clamp(point.x, -BOARD_HALF, BOARD_HALF), y: clamp(point.y, L.hoop.y + 0.2, BOARD_TOP - 0.1), z: L.backboard.z + 0.4 }, apexY: Math.max(L.flightApexY, point.y + 2), missDirection: side };
  }
  const impact = { x: clamp(point.x, -6, 6), y: clamp(point.y, R.minReachY, 9), z: L.hoop.z };
  return { outcome: 'airball', impact, apexY: Math.max(L.flightApexY, impact.y + 2.5), missDirection: side };
}

/** Yanlış cevabın otomatik atışı: `random` [0,1) ile çember veya panyadan dönen, basket olmayan atış. */
export function autoMissPlan(random: number): BasketShotPlan {
  const index = Math.min(4, Math.max(0, Math.floor(random * 5)));
  const offset = basketMissOffset(index);
  const outcome: BasketShotOutcome = index === 2 ? 'backboard' : 'rim';
  return {
    outcome,
    impact: { x: L.hoop.x + offset.x, y: L.hoop.y + offset.y, z: L.hoop.z + offset.z },
    apexY: L.flightApexY,
    missDirection: Math.sign(offset.x) || 1,
  };
}

export function moveBasketKeyboardAim(point: BasketAimPoint, dx: number, dy: number): BasketAimPoint {
  return {
    x: clamp(point.x + dx * R.keyboardStep, L.hoop.x - 3, L.hoop.x + 3),
    y: clamp(point.y + dy * R.keyboardStep, R.minReachY, BOARD_TOP + 1),
  };
}

export const initialBasketKeyboardAim: BasketAimPoint = { x: L.hoop.x, y: L.hoop.y - 0.3 };

/** Basket bölgesinin dört köşesi (pota düzleminde), ipucu çerçevesi için. */
export const basketTargetCorners: readonly BasketAimVector[] = [
  { x: L.hoop.x - R.basketHalfWidth, y: L.hoop.y - R.basketBelow, z: L.hoop.z },
  { x: L.hoop.x - R.basketHalfWidth, y: L.hoop.y + R.basketAbove, z: L.hoop.z },
  { x: L.hoop.x + R.basketHalfWidth, y: L.hoop.y + R.basketAbove, z: L.hoop.z },
  { x: L.hoop.x + R.basketHalfWidth, y: L.hoop.y - R.basketBelow, z: L.hoop.z },
];
